import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getUrl, ResourceFactory, ResourceUri } from '@ngxp/rest';
import { ApiRootLinkRel, ApiRootResource } from '@schir-int-client/api-root';
import { UserResource } from '@schir-int-client/user-shared';
import { saveAs } from 'file-saver';
import { CONTENT_TYPE__URI_LIST } from '@schir-int-client/tech';
import { Observable, of } from 'rxjs';
import { VerfahrenLinkRel, VerfahrenListLinkRel, ZaehlblattEntryListLinkRel } from './verfahren.linkrel';
import {
	NewVerfahren,
	RegisterAusdruckType,
	VerfahrenListResource,
	VerfahrenReferenzListResource,
	VerfahrenResource,
	VerfahrenSearchQuery,
	ZaehlblattAktionen,
	ZaehlblattEntryListResource,
	ZaehlblattEntryResource,
} from './verfahren.model';
import { UnterscheidungssignalResource } from '@schir-int-client/admin-shared';

@Injectable({ providedIn: 'root' })
export class VerfahrenService {
	emptyResource: VerfahrenListResource = {
		_embedded: { verfahrenList: [] },
		_links: {
			self: {
				href: '',
			},
		},
		page: {
			totalElements: 0,
			size: 0,
			totalPages: 0,
		},
	};

	constructor(
		private resourceFactory: ResourceFactory,
		private httpClient: HttpClient
	) {}

	public getAll(rootResource: ApiRootResource): Observable<VerfahrenListResource> {
		try {
			return this.resourceFactory.from(rootResource).get(ApiRootLinkRel.VERFAHRENS);
		} catch (e) {
			return of(this.emptyResource);
		}
	}

	public getOne(verfahrenUri: string): Observable<VerfahrenResource> {
		return this.resourceFactory.fromId(verfahrenUri).get();
	}

	public searchVerfahren(
		rootResource: ApiRootResource,
		query: VerfahrenSearchQuery
	): Observable<VerfahrenListResource> {
		let url = getUrl(rootResource, VerfahrenLinkRel.SEARCH_VERFAHREN)
			.replace('{searchBy}', encodeURIComponent(query.searchString))
			.replace('{searchIn}', query.searchIn.toString())
			.replace('{filterBy}', query.filterByStatus.toString());

		if (query.limit) {
			url = url.replace(/limit=\d+/, 'limit=' + query.limit);
		}

		return this.resourceFactory.fromId(url).get();
	}

	public getLastOpenedVerfahren(apiRoot: ApiRootResource): Observable<VerfahrenListResource> {
		return this.resourceFactory.from(apiRoot).get(ApiRootLinkRel.LAST_OPENED_VERFAHRENS);
	}

	public createVerfahren(
		verfahren: NewVerfahren,
		verfahrenList: VerfahrenListResource
	): Observable<VerfahrenResource> {
		return this.resourceFactory.from(verfahrenList).post(VerfahrenListLinkRel.ADD_VERFAHREN, verfahren);
	}

	public saveRegisterAufschrift(verfahren: VerfahrenResource, aufschrift: string): Observable<VerfahrenResource> {
		return this.resourceFactory.fromId(getUrl(verfahren, VerfahrenLinkRel.AUFSCHRIFT)).patch({ aufschrift });
	}

	public updateNotiz(notiz: string, verfahrenUri: ResourceUri): Observable<VerfahrenResource> {
		return this.resourceFactory.fromId(verfahrenUri).patch({ notiz });
	}

	public markAsAbgelehnt(verfahren: VerfahrenResource): Observable<VerfahrenResource> {
		return this.resourceFactory.from(verfahren).patch(VerfahrenLinkRel.MARK_AS_ABGELEHNT, { status: 'ABGELEHNT' });
	}

	public delete(verfahren: VerfahrenResource): Observable<VerfahrenListResource> {
		return this.resourceFactory.from(verfahren).delete(VerfahrenLinkRel.DELETE);
	}

	public downloadAusdruck(type: RegisterAusdruckType, verfahren: VerfahrenResource) {
		let linkRel;
		switch (type) {
			case 'beglaubigt':
				linkRel = VerfahrenLinkRel.BEGLAUBIGTER_REGISTERBLATT_AUSDRUCK;
				break;
			case 'einfach':
				linkRel = VerfahrenLinkRel.REGISTERBLATT_AUSDRUCK;
				break;
			case 'entwurf':
				linkRel = VerfahrenLinkRel.REGISTERBLATT_AUSDRUCK_ENTWURF;
				break;
			default:
				throw new Error('Invalid type');
		}
		return this.downloadDocument(getUrl(verfahren, linkRel));
	}

	public downloadAmtlicherAuszug(verfahren: VerfahrenResource) {
		return this.downloadDocument(getUrl(verfahren, VerfahrenLinkRel.REGISTERBLATT_AMTLICHER_AUSZUG));
	}

	public downloadSchiffszertifikat(verfahren: VerfahrenResource) {
		return this.downloadDocument(getUrl(verfahren, VerfahrenLinkRel.REGISTERBLATT_SCHIFFSZERTIFIKAT));
	}

	public downloadSchiffsbrief(verfahren: VerfahrenResource) {
		return this.downloadDocument(getUrl(verfahren, VerfahrenLinkRel.REGISTERBLATT_SCHIFFSBRIEF));
	}

	private downloadDocument(url: string): Observable<Blob> {
		let headers = new HttpHeaders();
		headers = headers.set('Accept', 'application/pdf, application/hal+json');

		return this.httpClient.get<Blob>(url, { headers, responseType: 'blob' as 'json' });
	}

	public saveFile(data: Blob, fileName: string) {
		saveAs(data, fileName);
	}

	public verfahrenBearbeiten(verfahren: VerfahrenResource, user: UserResource): Observable<VerfahrenResource> {
		return this.httpClient.patch<VerfahrenResource>(getUrl(verfahren, VerfahrenLinkRel.BEARBEITEN), getUrl(user), {
			headers: CONTENT_TYPE__URI_LIST,
		});
	}

	public verfahrenUebernehmen(verfahren: VerfahrenResource, user: UserResource): Observable<VerfahrenResource> {
		return this.httpClient.patch<VerfahrenResource>(getUrl(verfahren, VerfahrenLinkRel.UEBERNEHMEN), getUrl(user), {
			headers: CONTENT_TYPE__URI_LIST,
		});
	}

	public verfahrenBearbeitenBeenden(verfahren: VerfahrenResource): Observable<VerfahrenResource> {
		return this.httpClient.delete<VerfahrenResource>(getUrl(verfahren, VerfahrenLinkRel.BEARBEITEN_BEENDEN));
	}

	public assignUsignal(verfahren: VerfahrenResource): Observable<HttpResponse<UnterscheidungssignalResource>> {
		return this.httpClient.patch<UnterscheidungssignalResource>(
			getUrl(verfahren, VerfahrenLinkRel.ASSIGN_USIGNAL),
			{},
			{ observe: 'response' }
		);
	}

	setVerfahrenProperty(verfahren: VerfahrenResource, linkRel: string, value: string): Observable<VerfahrenResource> {
		return this.httpClient.patch<VerfahrenResource>(getUrl(verfahren, linkRel), value);
	}

	verfahrenszuordnungSearch(
		verfahren: VerfahrenResource,
		searchBy: string,
		limit?: number
	): Observable<VerfahrenReferenzListResource> {
		const url = getUrl(verfahren, VerfahrenLinkRel.ZUGEORDNETE_VERFAHREN_SEARCH)
			.replace('{searchBy}', searchBy)
			.replace('{&limit}', limit ? `&limit=${limit}` : '');
		return this.httpClient.get<VerfahrenReferenzListResource>(url);
	}

	setZugeordneteVerfahren(
		verfahren: VerfahrenResource,
		zugeordneteVerfahren: number[]
	): Observable<VerfahrenResource> {
		return this.httpClient.put<VerfahrenResource>(
			getUrl(verfahren, VerfahrenLinkRel.ZUGEORDNETE_VERFAHREN),
			zugeordneteVerfahren
		);
	}

	addZaehlblattAktionen(
		values: ZaehlblattAktionen,
		zaehlblatt: ZaehlblattEntryListResource
	): Observable<ZaehlblattEntryListResource> {
		return this.httpClient.post<ZaehlblattEntryListResource>(
			getUrl(zaehlblatt, ZaehlblattEntryListLinkRel.ADD_AKTIONEN),
			values
		);
	}

	deleteZaehlblattAktion(entry: ZaehlblattEntryResource): Observable<ZaehlblattEntryListResource> {
		return this.httpClient.delete<ZaehlblattEntryListResource>(
			getUrl(entry, ZaehlblattEntryListLinkRel.DELETE_AKTION)
		);
	}

	loadZaehlblatt(verfahren: VerfahrenResource): Observable<ZaehlblattEntryListResource> {
		return this.httpClient.get<ZaehlblattEntryListResource>(getUrl(verfahren, VerfahrenLinkRel.ZAEHLBLATT));
	}

	exportVerfahren(url: string): Observable<Blob> {
		let headers = new HttpHeaders();
		headers = headers.set('Accept', 'application/hal+json');
		return this.httpClient.get<Blob>(url, { headers, responseType: 'blob' as 'json' });
	}
}
